/*
 * @providesModule SkillzStyle
 *
 */
import SKZAPIConstants from 'SKZAPIConstants';
import ClientPropertyConstants from 'ClientPropertyConstants';

import _ from 'lodash';

import theme from './themes/cobalt-theme.json';
import ThemeOverrideProps, {AdditionalThemeProps} from 'ThemeOverrideProps';
import FallbackSkillzThemeProps from './FallbackSkillzThemeProps';

// eslint-disable-next-line

import {NativeModules, Platform, processColor, StyleSheet} from 'react-native';
import CommonStyles from './CommonStyles';
import {navigationRef} from 'NavigationRef';

const sprintf = require('sprintf-js').sprintf;

const ReactStyleManager = NativeModules.SKZReactStyleManager;

// we need two different types because SkillzStyle do not have fullStyles on obj initiation.
// we need to call .initialize() because fullStyles is added, so we are casting on export
interface SkillzStyleBaseType {
  initialize: () => void;
  convertToOpacity: (color: string, opacity: number) => string;
  convertOpacityToColor: (color: string, alpha: number, background: string) => string;
  desaturate: (rgba: string, value: number) => string;
  isThemeImage: (imageName: string) => boolean;
  useValueForTheme: <T extends number | string>(lightValue: T, darkValue: T) => T;
  themeHasBackgroundImage: () => boolean;
  /**
   * Returns the full styles with theme overrides applied for iOS.
   * Android uses the full theme styles with a few OpenSans font families supported by the Native side on older versions.
   */
  getOverriddenTheme: () => FullStylesType;
  /**
   * @deprecated this function is not used. Marking as deprecated for removal later.
   */
  searchObj: (obj: any, value: any) => boolean;
  /**
   * Fallback theme overrides for the FTUE screens only
   */
  getOverriddenFallbackTheme: () => FullStylesType;
  /**
   * Check if current screen matches one of the specified FTUE screens
   */
  isCurrentScreenInFTUEList: () => boolean;

  cleanObjectOfKey: <T>(obj: T, key: string) => T;
}

// Import CommonStyles type
type CommonStylesType = typeof CommonStyles;

enum SCREENS {
  SignInView = 'SignInView',
  SignUpView = 'SignUpView',
  WelcomeBonusScreen = 'WelcomeBonusScreen',
  OTPInputField = 'OTPInputField',
  EmailVerification = 'EmailVerification',
  MergeAccountScreen = 'MergeAccountScreen',
}

type FullStylesType = typeof theme.sheetStyles &
  typeof theme.gradients &
  typeof theme.opacityLevels &
  typeof theme.currencyValues &
  typeof theme.enums &
  typeof theme.images &
  AdditionalThemeProps &
  CommonStylesType;

interface SkillzStyleExportedType extends SkillzStyleBaseType {
  fullStyles: FullStylesType;

  sheetStyles: typeof theme.sheetStyles;
  gradients: typeof theme.gradients;
  opacityLevels: typeof theme.opacityLevels;
  currencyValues: typeof theme.currencyValues;
  enums: typeof theme.enums;
  images: typeof theme.images;

  themeJSON: typeof theme;
}

const SkillzStyle: SkillzStyleBaseType = {
  initialize() {
    let styleData = null;

    if (Platform.OS === 'android') {
      try {
        // console.debug(ReactStyleManager.ThemeJSON);
        styleData = JSON.parse(ReactStyleManager.ThemeJSON);
        console.debug('ANDROID');
        // console.debug(styleData);
      } catch (e) {
        console.warn('caught exception in SkillzStyle.initialize()');
      }
    } else if (Platform.OS === 'web') {
      const dynamicTheme = ReactStyleManager.getDynamicTheme();
      styleData = !!Object.keys(dynamicTheme).length ? dynamicTheme : theme;
    } else {
      styleData = ReactStyleManager.Theme;
    }
    this.themeJSON = styleData;
    styleData = this.cleanObjectOfKey(styleData, 'description');

    this.sheetStyles = styleData.sheetStyles;
    this.gradients = styleData.gradients;
    this.opacityLevels = styleData.opacityLevels;
    this.currencyValues = styleData.currencyValues;
    this.enums = styleData.enums;
    this.images = styleData.images;

    let fullStyles: FullStylesType = Object.assign(
      {},
      styleData.sheetStyles,
      styleData.gradients,
      styleData.opacityLevels,
      styleData.currencyValues,
      styleData.enums,
      styleData.images,
      CommonStyles
    );

    /**
     * Overrides non-managed theme properties like font families if the toggle is enabled.
     * This approach circumvents the need for update jobs and the logic on custom themes creation via the Developer Console.
     * It also saves us to update the theme file manually on s3 as mentioned on the TDD bellow.
     *
     * https://skillzinc.atlassian.net/wiki/spaces/PDS/pages/2177761835/TDD+Open+Sans+-+Font+Migration+main-2024
     */
    if (ClientPropertyConstants.getTournamentTemplateUIV2Enabled() && Platform.OS !== 'android') {
      fullStyles = {
        ...fullStyles,
        ...ThemeOverrideProps.sheetStyles,
      };
      this.sheetStyles = {
        ...this.sheetStyles,
        ...ThemeOverrideProps.sheetStyles,
      };
    } else {
      fullStyles = {
        ...fullStyles,
        fontSemiBold: {
          fontFamily: 'HelveticaNeue-Medium',
        },
        fontExtraBold: {
          fontFamily: 'HelveticaNeue-Bold',
        },
      };
      this.sheetStyles = {
        ...this.sheetStyles,
        fontSemiBold: {
          fontFamily: 'HelveticaNeue-Medium',
        },
        fontExtraBold: {
          fontFamily: 'HelveticaNeue-Bold',
        },
      };
    }

    this.fullStyles = fullStyles;

    //Remove descriptions
    this.styleSheet = StyleSheet.create(styleData.sheetStyles);
    const themedItems = new Set();

    function recurse(obj: Record<string, string> | string) {
      if (_.isString(obj)) {
        themedItems.add(obj);
      } else {
        for (const key in obj) {
          recurse(obj[key]);
        }
      }
    }

    recurse(styleData);
    this.themedItems = themedItems;
  },

  // Override theme only when current screen is in FTUE screens list
  getOverriddenFallbackTheme(): FullStylesType {
    if (ClientPropertyConstants.getFTUEV2FeatureEnabled()) {
      // render fallback on the basis of theme
      const {statusBarContentStyle} = this.fullStyles;
      const isDarkTheme = statusBarContentStyle === 0;

      const shouldUseFallbackTheme = ClientPropertyConstants.getFTUEUseSkillzTheme();
      if (shouldUseFallbackTheme) {
        if (isDarkTheme) {
          return {
            ...this.fullStyles,
            ...FallbackSkillzThemeProps.FallbackSkillsDarkThemeProps.sheetStyles,
            ...FallbackSkillzThemeProps.FallbackSkillsDarkThemeProps.gradients,
            ...FallbackSkillzThemeProps.FallbackSkillsDarkThemeProps.images,
          };
        } else {
          return {
            ...this.fullStyles,
            ...FallbackSkillzThemeProps?.FallbackSkillsLightThemeProps.sheetStyles,
            ...FallbackSkillzThemeProps?.FallbackSkillsLightThemeProps.gradients,
            ...FallbackSkillzThemeProps?.FallbackSkillsLightThemeProps.images,
          };
        }
      }
    }
    return this.fullStyles;
  },

  isCurrentScreenInFTUEList(): boolean {
    try {
      // Get current screen from React Navigation
      const currentRouteName = navigationRef?.current?.getCurrentRoute?.()?.name;

      // Check if current route matches any of the FTUE screens
      const ftueScreens = [
        SCREENS.SignInView,
        SCREENS.SignUpView,
        SCREENS.WelcomeBonusScreen,
        SCREENS.OTPInputField,
        SCREENS.EmailVerification,
        SCREENS.MergeAccountScreen,
      ];
      return ftueScreens.includes(currentRouteName as any);
    } catch (error) {
      console.warn('Error checking current screen in SkillzStyle:', error);
      return false;
    }
  },

  getOverriddenTheme(): FullStylesType {
    const isThemeOverrideEnabled = ClientPropertyConstants.getTournamentTemplateUIV2Enabled();

    if (isThemeOverrideEnabled) {
      return Platform.select<FullStylesType>({
        ios: {
          ...this.fullStyles,
          ...ThemeOverrideProps.sheetStyles,
        },
        /**
         * For Android, we only override the font families that are supported
         * by the Android Native SDK on lower SDK versions.
         */
        android: {
          ...this.fullStyles,
          ...ThemeOverrideProps.sheetStyles.fontRegular,
          ...ThemeOverrideProps.sheetStyles.fontBold,
          ...ThemeOverrideProps.sheetStyles.textColorCash,
          ...ThemeOverrideProps.sheetStyles.textColorTicketz,
        },
        default: this.fullStyles,
      });
    }

    // Return the default theme for all other cases
    return this.fullStyles;
  },

  convertToOpacity(color: string = '#000', opacity: number): string {
    const rgb = color.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
    let r = '',
      g = '',
      b = '';
    if (rgb && rgb.length == 4) {
      r = rgb[1];
      g = rgb[2];
      b = rgb[3];
    } else {
      color = color.replace('#', '');
      r = parseInt(color.substring(0, 2), 16).toString();
      g = parseInt(color.substring(2, 4), 16).toString();
      b = parseInt(color.substring(4, 6), 16).toString();
    }
    const result = 'rgba(' + r + ',' + g + ',' + b + ',' + opacity + ')';
    return result;
  },

  // color is given in rgb/rgba format
  convertOpacityToColor(color: string, alpha: number, background: string = '#FFF'): string {
    const kWhiteColor = 0xffffff;
    const kBlackColor = 0x000000;

    const rgb = processColor(color) ?? kWhiteColor;
    const backRgb = processColor(background) ?? kBlackColor;

    const byteShift = 8;

    const redFore = (rgb >> (2 * byteShift)) & ONE_BYTE_MASK;
    const greenFore = (rgb >> (1 * byteShift)) & ONE_BYTE_MASK;
    const blueFore = rgb & ONE_BYTE_MASK;

    const redBack = (backRgb >> (2 * byteShift)) & ONE_BYTE_MASK;
    const greenBack = (backRgb >> (1 * byteShift)) & ONE_BYTE_MASK;
    const blueBack = backRgb & ONE_BYTE_MASK;

    const inverseAlpha = 1 - alpha;

    const red = redFore * alpha + redBack * inverseAlpha;
    const green = greenFore * alpha + greenBack * inverseAlpha;
    const blue = blueFore * alpha + blueBack * inverseAlpha;

    const result = `rgb(${red},${green},${blue})`;
    return result;
  },

  // Desaturate works on colors with rgba('0,0,0,1') format
  // Best values to use are between 0 and 1
  desaturate(rgba: string, value: number): string {
    const rgbaArray = rgba
      .substring(5, rgba.length - 1)
      .replace(/ /g, '')
      .split(',');
    const r = parseInt(rgbaArray[0]);
    const g = parseInt(rgbaArray[1]);
    const b = parseInt(rgbaArray[2]);

    const L = 0.3 * r + 0.6 * g + 0.1 * b;
    const newR = r + value * (L - r);
    const newG = g + value * (L - g);
    const newB = b + value * (L - b);

    return `rgba(${newR}, ${newG}, ${newB}, ${rgbaArray[3]})`;
  },

  isThemeImage(imageName: string): boolean {
    return this.themedItems.has(imageName);
  },

  useValueForTheme(lightValue, darkValue) {
    const isLightTheme = this.enums?.statusBarContentStyle ?? 0;
    return isLightTheme == 1 ? lightValue : darkValue;
  },

  themeHasBackgroundImage(): boolean {
    return this.images.backgroundImage != null && this.images.backgroundImage.length > 0;
  },

  /**
   * @deprecated this function is not used. Marking as deprecated for removal later.
   */
  searchObj(obj, value) {
    let result = false;
    for (const key in obj) {
      if (typeof obj[key] === 'object') {
        result = result || this.searchObj(obj[key], value);
      } else if (obj[key] == value) {
        return true;
      }
    }
    return result;
  },

  cleanObjectOfKey(obj, key) {
    for (const i in obj) {
      if (!obj.hasOwnProperty(i)) {
        continue;
      }

      if (typeof obj[i] === 'object') {
        obj[i] = this.cleanObjectOfKey(obj[i], key);
      } else if (i == key) {
        delete obj[key];
      }
    }
    return obj;
  },
};

const ONE_BYTE_MASK = 0xff;
const MAX_COLOR_VALUE = 0xff;

/* eslint-disable no-magic-numbers */
export function lightenColor(colorProp: any, amount?: number): string {
  const color = processColor(colorProp);
  const DEFAULT_INCREMENT = 26;

  let r = (color >> 16) & ONE_BYTE_MASK;
  let g = (color >> 8) & ONE_BYTE_MASK;
  let b = color & ONE_BYTE_MASK;

  const increment = amount != null ? Math.round(DEFAULT_INCREMENT * amount) : DEFAULT_INCREMENT;

  r = Math.min(MAX_COLOR_VALUE, r + increment);
  g = Math.min(MAX_COLOR_VALUE, g + increment);
  b = Math.min(MAX_COLOR_VALUE, b + increment);

  return sprintf('#%02x%02x%02x', r, g, b);
}

export function darkenColor(colorProp: any): string {
  const color = processColor(colorProp);

  let r = (color >> 16) & ONE_BYTE_MASK;
  let g = (color >> 8) & ONE_BYTE_MASK;
  let b = color & ONE_BYTE_MASK;

  r = Math.max(0, r - 25);
  g = Math.max(0, g - 25);
  b = Math.max(0, b - 25);

  return sprintf('#%02x%02x%02x', r, g, b);
}

export function inverseColor(colorProp: any): string {
  const color = processColor(colorProp);
  let r = (color >> 16) & ONE_BYTE_MASK;
  let g = (color >> 8) & ONE_BYTE_MASK;
  let b = color & ONE_BYTE_MASK;

  r = 255 - r;
  g = 255 - g;
  b = 255 - b;

  return sprintf('#%02x%02x%02x', r, g, b);
}

// add two colors with alpha
export function addColors(base: string, over: string): string {
  const c1 = processColor(base);
  const c2 = processColor(over);

  const a1 = ((c1 >> 24) & ONE_BYTE_MASK) / MAX_COLOR_VALUE;
  const r1 = ((c1 >> 16) & ONE_BYTE_MASK) / MAX_COLOR_VALUE;
  const g1 = ((c1 >> 8) & ONE_BYTE_MASK) / MAX_COLOR_VALUE;
  const b1 = (c1 & ONE_BYTE_MASK) / MAX_COLOR_VALUE;

  const a2 = ((c2 >> 24) & ONE_BYTE_MASK) / MAX_COLOR_VALUE;
  const r2 = ((c2 >> 16) & ONE_BYTE_MASK) / MAX_COLOR_VALUE;
  const g2 = ((c2 >> 8) & ONE_BYTE_MASK) / MAX_COLOR_VALUE;
  const b2 = (c2 & ONE_BYTE_MASK) / MAX_COLOR_VALUE;

  // color combining algorithm from here https://stackoverflow.com/a/727339
  const a = 1 - (1 - a1) * (1 - a2);
  const r = (r1 * a1) / a + (r2 * a2 * (1 - a1)) / a;
  const g = (g1 * a1) / a + (g2 * a2 * (1 - a1)) / a;
  const b = (b1 * a1) / a + (b2 * a2 * (1 - a1)) / a;
  return `rgba(${r * MAX_COLOR_VALUE},${g * MAX_COLOR_VALUE},${b * MAX_COLOR_VALUE},${a})`;
}

SkillzStyle.initialize();
export const skillzLogoURLExtension = SkillzStyle.useValueForTheme(
  'Skillz_Logo_Dark.png',
  'Skillz_Logo_Light.png'
);

export const chatIconSvg = SkillzStyle.useValueForTheme(
  'icon_chat_header_dark.svg',
  'icon_chat_header_light.svg'
);

export function getLogoBaseURL(): string {
  const SKZAPIManager = NativeModules.SKZAPIManager;
  const defaultURL = '';
  let env = '';
  switch (SKZAPIManager.CurrentEnvironment) {
    case SKZAPIManager.Environment.Prod:
      env = SKZAPIConstants.SKZ_API_BASE_PROD;
      break;
    case SKZAPIManager.Environment.QA:
      env = SKZAPIConstants.SKZ_API_BASE_QA;
      break;
    case SKZAPIManager.Environment.Sandbox:
      env = SKZAPIConstants.SKZ_API_BASE_SAND;
      break;
    case SKZAPIManager.Environment.Staging:
      env = SKZAPIConstants.SKZ_API_BASE_STAGE;
      break;
    case SKZAPIManager.Environment.ProxyTS:
      env = SKZAPIConstants.SKZ_API_BASE_PROXY_TS;
      break;
    default:
      env = defaultURL;
  }

  const baseURL = 'https://cdn.' + env + 'branding/';
  return baseURL;
}

export const getGradientColors = (textGradient: any): any => {
  if (Platform.OS === 'web') {
    return {
      color: textGradient[0],
    };
  }

  return {
    gradientColors: textGradient,
  };
};

export default SkillzStyle as SkillzStyleExportedType;
